import * as React from "react";
import { useDispatch } from "react-redux";
import { Box, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { getCookie, parseJwt } from "../api/apiHelper";
import { logout } from "../Authentication/authSlice";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { FONT_FAMILY, BAD_RED_TEXT, stripErrorPrefix } from "./designTokens";
import { CONTAINER_SX, CONTENT_PB } from "./FALayout";

// neura.css .neura-skeleton — a tinted block with a gradient sweeping across it. The design ships
// these on Sites, Stock, the H&S hub, Fleet, Devices and Team; every Field Admin page
// previously rendered nothing at all until its query resolved.
const SHIMMER = {
  position: "relative",
  overflow: "hidden",
  backgroundColor: "rgba(255,255,255,0.04)",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    transform: "translateX(-100%)",
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
    animation: "fa-shimmer 1.3s ease-out infinite",
  },
  "@keyframes fa-shimmer": { "100%": { transform: "translateX(100%)" } },
};

const VARIANTS = {
  line: { height: "12px", borderRadius: "6px", margin: "8px 0" },
  row: { height: "44px", borderRadius: "8px", margin: "6px 0" },
  tile: { height: "96px", borderRadius: "10px" },
};

export function FASkeleton({ variant = "row", sx }) {
  return <Box aria-hidden sx={{ ...SHIMMER, ...VARIANTS[variant], ...sx }} />;
}

// The design's loading block is a card of shimmer rows standing in for the list that's
// about to arrive, so the page keeps its shape instead of collapsing.
export function FASkeletonRows({ count = 5, tiles = 0 }) {
  return (
    <>
      {tiles > 0 ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: `repeat(${tiles}, 1fr)` }, gap: "10px", mb: "18px" }}>
          {Array.from({ length: tiles }, (_, i) => <FASkeleton key={i} variant="tile" />)}
        </Box>
      ) : null}
      <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", px: "12px", py: "8px" }}>
        {Array.from({ length: count }, (_, i) => <FASkeleton key={i} variant="row" />)}
      </Box>
    </>
  );
}

// team_id is a claim minted into the token at login, not something read per-request, so a
// session that started without one can never acquire it by asking again. Deactivating a team
// clears the claim in Userport and reactivating restores it, but neither touches a token
// already in the browser.
function tokenHasNoTeam() {
  const access = getCookie("access");
  if (!access) return false;
  const claim = parseJwt(access)?.team_id;
  return claim === null || claim === undefined;
}

// The design specifies no error state — it's a static mock — but "blank forever with no
// message and no way to retry" is not a defensible substitute for one.
export function FAErrorCard({ message, onRetry }) {
  const dispatch = useDispatch();
  // Offering "Try again" to a field admin whose token carries no team is offering the one
  // action that provably cannot work: the retry re-sends the same token and gets the same 403.
  // It also contradicts the message, which correctly tells them to sign out and back in. So
  // when the claim is the thing that is missing, give them the button that actually fixes it.
  const needsNewToken = tokenHasNoTeam();
  return (
    <Box
      sx={{
        backgroundColor: "rgba(239,68,68,0.05)",
        border: "1px solid rgba(239,68,68,0.18)",
        borderRadius: "12px",
        px: "20px",
        py: "24px",
        textAlign: "center",
      }}
    >
      <ErrorOutlineIcon sx={{ color: BAD_RED_TEXT, fontSize: "1.6rem", mb: "8px" }} />
      <Typography sx={{ color: "#fff", fontSize: "0.95rem", fontWeight: 600, mb: "4px" }}>
        Couldn&rsquo;t load this page
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem", mb: (onRetry || needsNewToken) ? "16px" : 0 }}>
        {message || "The request failed. Check your connection and try again."}
      </Typography>
      {needsNewToken ? (
        <Box
          component="button"
          type="button"
          onClick={() => dispatch(logout())}
          sx={{
            px: "16px", py: "9px", borderRadius: "9px", cursor: "pointer",
            backgroundColor: "rgba(253,185,64,0.1)", border: "1px solid rgba(253,185,64,0.3)",
            color: AMBER, fontWeight: 600, fontSize: "0.84rem", fontFamily: FONT_FAMILY,
          }}
        >
          Sign out
        </Box>
      ) : onRetry ? (
        <Box
          component="button"
          type="button"
          onClick={onRetry}
          sx={{
            px: "16px", py: "9px", borderRadius: "9px", cursor: "pointer",
            backgroundColor: "rgba(253,185,64,0.1)", border: "1px solid rgba(253,185,64,0.3)",
            color: AMBER, fontWeight: 600, fontSize: "0.84rem", fontFamily: FONT_FAMILY,
          }}
        >
          Try again
        </Box>
      ) : null}
    </Box>
  );
}

// Uses the same container as the content it stands in for — previously a second copy of
// that sx lived here, so the skeleton and the resolved page could drift apart and the
// layout would visibly shift as the query landed.
// Statuses where the backend wrote a message the field admin can act on: 403 is
// "you are not assigned to a team yet — ask your PM", 409 is "this day is signed off,
// reopen it first". Those are worth showing verbatim.
//
// Everything else keeps the generic connection copy on purpose. A 500 or a dropped
// request has no message a user can act on, and the raw one names the view class that
// raised it — "Couldn't load this page, check your connection" is both truer and safer.
const ACTIONABLE_STATUSES = [403, 409];

export function faErrorMessage(error) {
  if (!error || !ACTIONABLE_STATUSES.includes(error.status)) return null;
  // The backend prefixes error_message with a timestamp and the view that raised it.
  return stripErrorPrefix(error?.data?.error_message);
}

export function FAPageState({ isError, error, onRetry, count = 5, tiles = 0 }) {
  return (
    <Box sx={{ ...CONTAINER_SX, pb: CONTENT_PB }}>
      {isError ? (
        <FAErrorCard message={faErrorMessage(error)} onRetry={onRetry} />
      ) : (
        <FASkeletonRows count={count} tiles={tiles} />
      )}
    </Box>
  );
}

// The inline error line under a dialog's form fields. Three pages rendered this exact
// Typography verbatim, each with its own local copy of stripErrorPrefix behind it.
export function FAFieldError({ error, fallback }) {
  if (!error) return null;
  return (
    <Typography sx={{ color: BAD_RED_TEXT, fontSize: "0.8rem", mt: "14px" }}>
      {stripErrorPrefix(error?.data?.error_message) || fallback || "Something went wrong."}
    </Typography>
  );
}
